import type { ChatRole, FeedMessage } from "@/types";
import { CLEVERENCE_AUTHOR, isCleverenceAuthor } from "@/constants/cleverence";
import { getSessionId } from "@/utils/session";
import { participantLabel } from "@/utils/anonymousAuthor";

const LIVE_STATE_KEY = "ai_chat_live_state";

export interface LiveChatState {
  userAuthor: string;
  assistantAuthor: string;
  messages: FeedMessage[];
  dialogue: ChatRole[];
  nextId: number;
}

const DEFAULT_STATE: LiveChatState = {
  userAuthor: "",
  assistantAuthor: "",
  messages: [],
  dialogue: [],
  nextId: -1,
};

export function loadLiveState(): LiveChatState {
  try {
    const raw = sessionStorage.getItem(LIVE_STATE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as LiveChatState;
    const messages = (Array.isArray(parsed.messages) ? parsed.messages : []).map(
      (m) => normalizeCleverenceMessage(m)
    );
    return {
      ...DEFAULT_STATE,
      ...parsed,
      assistantAuthor: CLEVERENCE_AUTHOR,
      messages,
      dialogue: Array.isArray(parsed.dialogue) ? parsed.dialogue : [],
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function saveLiveState(state: LiveChatState): void {
  sessionStorage.setItem(
    LIVE_STATE_KEY,
    JSON.stringify({
      ...state,
      messages: state.messages.slice(-40),
      dialogue: state.dialogue.slice(-20),
    })
  );
}

export async function ensureAuthors(
  state: LiveChatState
): Promise<LiveChatState> {
  const sessionId = getSessionId();
  const userAuthor = state.userAuthor || (await participantLabel(sessionId));
  const assistantAuthor = CLEVERENCE_AUTHOR;

  return { ...state, userAuthor, assistantAuthor };
}

export function clearLiveState(): void {
  sessionStorage.removeItem(LIVE_STATE_KEY);
}

/** Старые сессии: ответы AI были как Участник_XXXX */
function normalizeCleverenceMessage(msg: FeedMessage): FeedMessage {
  if (msg.is_live && (msg.is_typing || msg.reply_to != null)) {
    return {
      ...msg,
      author: CLEVERENCE_AUTHOR,
      author_kind: "bot",
    };
  }
  if (isCleverenceAuthor(msg.author)) {
    return { ...msg, author: msg.author, author_kind: "bot" };
  }
  return msg;
}
